import Modal from '@ui/Modal/component'
import initSelectCustom from '@ui/SelectCustom/component'
import { createFetch } from 'assets/core/js/common'
import type { ModalType } from '@ui/Modal/component'

const REQUEST_TIMEOUT = 30000

export interface SearchAroundSubmit {
  city: string
  maxDistance: string
}

export interface SearchAroundConfig {
  onSubmit?: (submit: SearchAroundSubmit) => void
}

const setVisible = (element: HTMLElement, visible: boolean): void => {
  element.setAttribute('data-visible', String(visible))
}

const toggle = (element: HTMLElement, visible: boolean): void => {
  element.hidden = !visible
}

const setMessage = (element: HTMLElement, message: string): void => {
  const messageEl = element.querySelector<HTMLElement>('.o-message__message')

  if (messageEl) {
    messageEl.textContent = message
  }
}

export default function (id: string, config: SearchAroundConfig = {}): ModalType | undefined {
  const modalEl = document.getElementById(`${id}-modal`)

  if (!modalEl) {
    return
  }

  const modal: ModalType = Modal(modalEl)

  const distanceEl = modalEl.querySelector<HTMLDetailsElement>('details.o-select-custom')
  const distanceSelectEl = distanceEl?.querySelector<HTMLSelectElement>('select')

  if (distanceEl) {
    initSelectCustom(distanceEl)
  }

  const buttonEl = document.getElementById(`${id}-geoloc`) as HTMLButtonElement | null
  const valueEl = document.getElementById(`${id}-value`) as HTMLInputElement | null
  const submitEl = document.getElementById(`${id}-submit`)
  const infoEl = modalEl.querySelector<HTMLElement>('.search-around__geoloc-info')
  const successEl = modalEl.querySelector<HTMLElement>('.search-around__geoloc-success')
  const warningEl = modalEl.querySelector<HTMLElement>('.search-around__geoloc-warning')
  const cityEl = modalEl.querySelector<HTMLElement>('.search-around__geoloc-city')
  const clearEl = modalEl.querySelector<HTMLElement>('.search-around__clear')

  if (!buttonEl || !valueEl || !submitEl || !infoEl || !successEl || !warningEl || !cityEl || !clearEl) {
    return modal
  }

  submitEl.addEventListener('click', function () {
    modal.close()
    config.onSubmit?.({ city: valueEl.value, maxDistance: distanceSelectEl?.value ?? '' })
  })

  if (!navigator.geolocation) {
    toggle(buttonEl, false)

    return modal
  }

  let watchdog: number | undefined

  const stopWatchdog = (): void => {
    window.clearTimeout(watchdog)
    watchdog = undefined
  }

  const { url, errorMessage, notFoundMessage } = buttonEl.dataset

  const showWarning = (message: string): void => {
    stopWatchdog()
    setVisible(infoEl, false)
    setMessage(warningEl, message)
    setVisible(warningEl, true)
  }

  const showSuccess = (city: string): void => {
    stopWatchdog()
    setVisible(infoEl, false)
    cityEl.textContent = city
    setVisible(successEl, true)
  }

  buttonEl.addEventListener('click', function () {
    if (buttonEl.contains(document.activeElement)) {
      valueEl.focus()
    }

    toggle(buttonEl, false)
    setVisible(successEl, false)
    setVisible(warningEl, false)
    setVisible(infoEl, true)

    stopWatchdog()
    watchdog = window.setTimeout(() => {
      setVisible(infoEl, false)
    }, REQUEST_TIMEOUT)

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          const locateUrl = new URL(url as string, window.location.origin)
          locateUrl.searchParams.set('latitude', String(position.coords.latitude))
          locateUrl.searchParams.set('longitude', String(position.coords.longitude))

          const response = await createFetch(locateUrl.toString())

          if (!response.ok) {
            showWarning(notFoundMessage as string)

            return
          }

          const { city } = (await response.json()) as { city: string | null }

          if (!city) {
            showWarning(notFoundMessage as string)

            return
          }

          valueEl.value = city
          toggle(clearEl, true)
          showSuccess(city)
        } catch {
          showWarning(notFoundMessage as string)
        }
      },
      () => {
        showWarning(errorMessage as string)
      },
      { enableHighAccuracy: true, timeout: REQUEST_TIMEOUT, maximumAge: 300000 }
    )
  })

  clearEl.addEventListener('click', function () {
    valueEl.value = ''
    setVisible(successEl, false)
    setVisible(warningEl, false)
    toggle(clearEl, false)
    toggle(buttonEl, true)
    valueEl.focus()
  })

  valueEl.addEventListener('input', function () {
    const hasValue = valueEl.value !== ''

    toggle(clearEl, hasValue)

    if (!hasValue) {
      toggle(buttonEl, true)
    }

    setVisible(successEl, false)
    setVisible(warningEl, false)
  })

  return modal
}
